import { createStore, createEffect, createEvent, combine, sample } from 'effector';
import i18n from 'i18n';
import { $notificationStore } from '../notificationStore';
import { ApiUtils } from '@shared/utils';
import { UsersApi } from '@shared/api/rest';
import type { DeleteUserCardsRequest, UpdatePasswordRequest, UserResponse, UserUpdateWithIdRequest } from '@shared/api/rest/users';
import { $userCardsStore } from './userCardsStore';
import { type IQueryParams, RequestStrategyEnum } from '@shared/api/models';

const getUserEffect = createEffect((params: IQueryParams<string>) => UsersApi.getUser(params.request, params?.signal));
const updateUserEffect = createEffect((request: UserUpdateWithIdRequest) => UsersApi.updateUser(request));
const changePasswordEffect = createEffect((request: UpdatePasswordRequest) => UsersApi.updatePassword(request));
const lockUserEffect = createEffect((params: IQueryParams<string>) => UsersApi.lockUser(params.request, params?.signal));
const unlockUserEffect = createEffect((params: IQueryParams<string>) => UsersApi.unlockUser(params.request, params?.signal));
const deleteUserEffect = createEffect((id: string) => UsersApi.deleteUser(id));
const deleteCardEffect = createEffect(async (params: { request: DeleteUserCardsRequest }) => {
  const user = await UsersApi.deleteUserCards(params.request);
  if (!user.success) throw new Error(user.message || 'Error deleting user');
  return { AssignedUserId: params.request.userIds };
});
const clearEvent = createEvent();
sample({
  source: deleteCardEffect.doneData,
  target: $userCardsStore.events.getUserCards,
});

const $dataStore = createStore<UserResponse | null>(null)
  .on(getUserEffect.done, (current, data) => data.result.data || current)
  .on(updateUserEffect.done, (current, data) => data.result.data || current)
  .on(lockUserEffect.done, (current) => {
    return current
      ? {
          ...current,
          locked: true,
        }
      : current;
  })
  .on(unlockUserEffect.done, (current) => {
    return current
      ? {
          ...current,
          locked: false,
        }
      : current;
  })
  .on(deleteUserEffect.done, () => null)
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getUserEffect.pending, (_, data) => data)
  .on(getUserEffect.fail, () => false)
  .on(getUserEffect.done, () => false)
  .on(deleteUserEffect.pending, (_, data) => data)
  .on(deleteUserEffect.fail, () => false)
  .on(deleteUserEffect.done, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getUserEffect, () => null)
  .on(getUserEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getUserEffect.done, () => null)
  .on(deleteUserEffect, () => null)
  .on(deleteUserEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(deleteUserEffect.done, () => null)
  .on(updateUserEffect, () => null)
  .on(updateUserEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(updateUserEffect.done, () => null)
  .on(changePasswordEffect, () => null)
  .on(changePasswordEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(changePasswordEffect.done, () => null)
  .on(lockUserEffect, () => null)
  .on(lockUserEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(lockUserEffect.done, () => null)
  .on(unlockUserEffect, () => null)
  .on(unlockUserEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(unlockUserEffect.done, () => null)
  .on(deleteCardEffect, () => null)
  .on(deleteCardEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(deleteCardEffect.done, () => null)
  .reset(clearEvent);
$errorStore.watch((x) => ApiUtils.errorStoreActionForbiddenWatcher(x, $notificationStore.events.setError));

export const $userStore = {
  events: {
    getUser: ApiUtils.wrapperApi(getUserEffect, RequestStrategyEnum.Debounce),
    updateUser: updateUserEffect,
    changePassword: changePasswordEffect,
    lockUser: ApiUtils.wrapperApi(lockUserEffect, RequestStrategyEnum.Debounce),
    unlockUser: ApiUtils.wrapperApi(unlockUserEffect, RequestStrategyEnum.Debounce),
    deleteUser: deleteUserEffect,
    deleteCard: ApiUtils.wrapperApi(deleteCardEffect, RequestStrategyEnum.Debounce),
    clear: clearEvent,
  },
  store: combine({
    data: $dataStore,
    loading: $loadingStore,
    error: $errorStore,
  }),
};
